import { Block, Value } from 'slate'
import { createElement } from './ElementPlugin'

/**
 * Serialize a Slate `value` to a plain text string.
 *
 * @param {Value} value
 * @param {Object} options
 * @return {String}
 */
const serialize = (value, options = {}) => {
  const { spacing } = options
  const result = serializeNode(value.document, options)

  if (!result) {
    return ''
  }

  return JSON.stringify(result, null, spacing)
}

/**
 * Serialize a `node` to plain text.
 *
 * @param {Node} node
 * @param {Object} options
 * @return {String}
 */
const serializeNode = (node, options = {}) => {
  if (node.object === 'document') {
    const questions = node.nodes
      .filter(node => (node.type === 'lineGroup' && !node.text ? false : node))
      .reduce((acc, node) => acc.concat(serializeNode(node)), [])
      .filter(node => node)

    // TODO: handle label
    return questions ? { title: options.title || '', label: '', questions } : {}
  } else if (node.object === 'block') {
    const getElementTypeText = qType => {
      const elementNodes = node
        .filterDescendants(node => node.type === qType)
        .map(node => node.text)
        .join('\n')

      if (elementNodes) {
        return elementNodes
      }
    }

    if (Block.isBlockList(node.nodes) || node.type === 'pageBreak') {
      // TODO: Add flag to answer nodes so we don't have to keep a comprehensive list
      const answerTypes = [
        'singleSelectAnswer',
        'multiSelectAnswer',
        'openAnswer',
        'numberAnswer',
        'pageBreak'
      ]
      const answers = node
        .filterDescendants(node => {
          return answerTypes.indexOf(node.type) > -1
        })
        .map(node => {
          // TODO: handle label, also a toJSON function on the node level would be good
          return { label: '', content: node.text }
        })
        .toArray()

      const question = { type: node.type }

      if (node.type === 'comment') {
        Object.assign(question, {
          label: getElementTypeText('elementLabel'),
          content: getElementTypeText('commentText')
        })
      } else if (node.type === 'lineGroup') {
        Object.assign(question, {
          content: getElementTypeText('line')
        })
      } else {
        Object.assign(question, {
          title: getElementTypeText('elementTitle'),
          label: getElementTypeText('elementLabel')
        })

        if (answers.length) {
          question.answers = { cols: [], rows: answers }
        }
      }

      question.data = node.data.toJSON()
      return question
    }
  }
}

/**
 * Converts a serialized survey into a SlateJS Value.
 *
 * @param {Object} data
 * @return Value
 */
const deserialize = (data, childTypesMap) => {
  const nodes = data.questions.map(q => {
    switch (q.type) {
      case 'lineGroup':
        return createBasicElement(q.type, 'line', q.content, q.data)
      case 'comment':
        return createBasicElement(q.type, 'commentText', q.content, q.data)
      case 'pageBreak':
        return Block.create({
          type: 'pageBreak'
        })
      default:
        const lines = [q.title].concat(
          q.answers ? q.answers.rows.map(row => row.content) : []
        )
        return createElement(q.type, lines, childTypesMap[q.type], q.data)
    }
  })

  if (!nodes.length) {
    nodes.push(
      Block.create({
        type: 'paragraph',
        nodes: [
          {
            object: 'text',
            leaves: [
              {
                text: ''
              }
            ]
          }
        ]
      })
    )
  }

  const finalNodes = nodes.filter(n => n)
  return Value.fromJSON({
    document: {
      nodes: finalNodes
    }
  })
}

/**
 * Creates a non complex element consisting of a parent type and a repeating child type.
 *
 * @param {String} type
 * @param {String} childType
 * @param {String} content
 * @return Block
 */
const createBasicElement = (type, childType, content, data = {}) => {
  return Block.create({
    type: type,
    data: data,
    nodes: content.split('\n').map(line => {
      return Block.create({
        type: childType,
        nodes: [
          {
            object: 'text',
            leaves: [
              {
                object: 'leaf',
                text: line
              }
            ]
          }
        ]
      })
    })
  })
}

const SerializerPlugin = options => {
  const childTypesMap = {}

  return {
    commands: {
      registerSerializer(editor, type, childTypes) {
        if (!childTypesMap[type]) {
          childTypesMap[type] = childTypes
        }
      }
    },
    queries: {
      serialize(editor, value, options) {
        return serialize(value, options)
      },
      deserialize(editor, data) {
        return deserialize(data, childTypesMap)
      }
    }
  }
}

export default SerializerPlugin



// WEBPACK FOOTER //
// ./src/components/Editor/plugins/SerializerPlugin.js